import type { ScriptStatus } from './types';

// The client-facing SCRIPT journey — and only the script journey. Per Henry's
// Phase 0 review, the review page tracks the script from draft to approval; the
// wider production journey (shoot day, editing, delivery) lives on the client
// dashboard, not here. So there are exactly four steps and no shoot/production
// state feeds into this at all.
//
// Four stages (client-review redesign, 2026-07-18): Script drafted → Version N
// finalized → Your review → Approved, with the first two always done by the
// time a client can see this page at all (a client never sees a 'draft'
// script — the route 423s to "being revised" first). The round number itself
// now surfaces here instead of as a tracker sub-label; the review card owns
// the "Round N · reply by" framing.

export type StepState = 'done' | 'current' | 'todo';

/** The one word that changes on an interview brief. */
export type ReviewKind = 'script' | 'brief';

export interface ProgressStep {
  key: 'drafted' | 'finalized' | 'review' | 'approved';
  label: string;
  state: StepState;
}

export interface Progress {
  steps: ProgressStep[];
  currentIndex: number;
}

const ROUND_NUMBER: Record<'v1' | 'v2' | 'v3', number> = { v1: 1, v2: 2, v3: 3 };

/**
 * The round number a status corresponds to (1/2/3). Used both for the
 * tracker's "Version N finalized" stage and the review card's "Round N"
 * status line. Falls back to 1 for 'draft' (never client-visible, handled
 * defensively) and for 'approved' — once a script is approved the status
 * itself no longer records which round it was approved at, so there's no
 * reliable N to report; callers should avoid printing a round number for the
 * approved state rather than guessing.
 */
export function roundNumber(status: ScriptStatus): number {
  if (status === 'v1' || status === 'v2' || status === 'v3') return ROUND_NUMBER[status];
  return 1;
}

/**
 * Script journey: Script drafted → Version N finalized → Your review →
 * Approved. Always returns four well-formed steps for any status, so the
 * tracker never crashes on unexpected data. The tracker is hidden on the
 * locked/draft page (a client never sees a draft), but 'draft' is handled
 * defensively — it maps to the same "your review is next" shape as v1.
 */
export function deriveProgress(status: ScriptStatus, kind: ReviewKind = 'script', publishedRound?: number | null): Progress {
  const drafted: ProgressStep = {
    key: 'drafted',
    label: kind === 'brief' ? 'Brief drafted' : 'Script drafted',
    state: 'done',
  };

  // The version a client is reviewing has, by definition, already been
  // finalized and sent — true for every client-visible status, including
  // 'approved' (everything before it is done). Only the label's number is
  // unknown for legacy approved copies without a published round.
  const round = publishedRound ?? (status === 'approved' ? null : roundNumber(status));
  const finalizedLabel = round === null ? 'Version finalized'
    : round > 3 ? `Round ${round}` : `Version ${round} finalized`;
  const finalized: ProgressStep = { key: 'finalized', label: finalizedLabel, state: 'done' };

  const review: ProgressStep = { key: 'review', label: 'Your review', state: 'todo' };
  const approved: ProgressStep = { key: 'approved', label: 'Approved', state: 'todo' };

  if (status === 'approved') {
    review.state = 'done';
    approved.state = 'current';
    return { steps: [drafted, finalized, review, approved], currentIndex: 3 };
  }

  // draft / v1 / v2 / v3 → the review is the live step.
  review.state = 'current';
  return { steps: [drafted, finalized, review, approved], currentIndex: 2 };
}
